import { z } from "zod";

import { sessionSchema } from "../schemas/session.schema";
import type { AuthSession } from "../types/auth.types";

export const AUTH_STORAGE_KEY = "kayer_auth_session";
const envelopeSchema = z.object({
  version: z.literal(1),
  session: sessionSchema,
});

/** Validates storage and accepts the original unversioned format for one-way migration. */
export function parseStoredSession(
  raw: string | null,
  now = Date.now(),
): AuthSession | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    const envelope = envelopeSchema.safeParse(value);
    // Unknown versioned envelopes cannot pass the legacy session schema.
    if (
      typeof value === "object" &&
      value !== null &&
      "version" in value &&
      !envelope.success
    )
      return null;
    const parsed = envelope.success
      ? envelope.data.session
      : sessionSchema.parse(value);
    return parsed.expiresAt > now ? parsed : null;
  } catch {
    return null;
  }
}

/** Storage failures allow in-memory login; malformed/expired data fails closed. */
export const authStorage = {
  loadSession(now = Date.now()): AuthSession | null {
    try {
      const session = parseStoredSession(
        localStorage.getItem(AUTH_STORAGE_KEY),
        now,
      );
      if (!session) authStorage.clearSession();
      else authStorage.saveSession(session);
      return session;
    } catch {
      return null;
    }
  },
  saveSession(session: AuthSession): void {
    try {
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({ version: 1, session }),
      );
    } catch {
      // Privacy restrictions/quota failures must not interrupt a valid in-memory login.
    }
  },
  clearSession(): void {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // In-memory logout still succeeds when browser storage is unavailable.
    }
  },
};
